import { flash, Modal, SubmitButton } from "@xola/ui-kit";
import { SecondaryButton } from "common";
import React from "react";
import { useMutation } from "react-query";
import { Event, Timeslot } from "types";
import { updateTimeslotCache } from "./EditEventCapacityModal";
import { getBlackoutSchedulePayload } from "./EventSchedulesHelper";
import { api } from "@/api/api";
import { useExperiences } from "@/hooks/resources/useProduct";
import { useAuthorization } from "@/hooks/useAuthorization";
import { removeCacheItem } from "@/utils/updateCache";

interface BlackoutMultipleEventsModalProps {
    events: Event[];
    timeslots: Timeslot[];
    onClose: (e: any) => void;
    isOpen: boolean;
}

export const BlackoutMultipleEventsModal = ({
    events = [],
    timeslots = [],
    onClose,
    isOpen,
}: BlackoutMultipleEventsModalProps) => {
    const hasSelectedEventsOrTimeslots = (events && events.length > 0) || (timeslots && timeslots.length > 0);

    if (!hasSelectedEventsOrTimeslots) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent timeslots={timeslots} events={events} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    events: Event[];
    timeslots: Timeslot[];
    onClose: (e?: any) => void;
}

const ModalContent = ({ events, timeslots, onClose }: ModalContentProps) => {
    const { isGranted } = useAuthorization();
    const { data: experiences } = useExperiences();

    const { mutateAsync: blackoutEvent, isLoading: blackoutPending } = useMutation(async (event: Event) => {
        try {
            const experience = experiences?.find((exp) => exp.id === event.experience.id);
            if (experience) {
                const payload = getBlackoutSchedulePayload({ event, experience });
                return await api.post(`/experiences/${experience.id}/schedules`, payload);
            } else {
                return null;
            }
        } catch {
            return null;
        }
    });

    const { mutateAsync: blackoutTimeslot, isLoading: blackoutTimeslotPending } = useMutation(
        async (timeslot: Timeslot) => {
            try {
                const experience = experiences?.find((exp) => exp.id === timeslot.product);
                if (experience) {
                    const payload = getBlackoutSchedulePayload({ timeslot, experience });
                    return await api.post(`/experiences/${experience.id}/schedules`, payload);
                } else {
                    return null;
                }
            } catch {
                return null;
            }
        },
    );

    const handleSubmit = async () => {
        try {
            const [blackoutEventResults, blackoutTimeslotResults] = await Promise.all([
                Promise.all(events.map(async (event) => blackoutEvent(event))),
                Promise.all(timeslots.map(async (timeslot) => blackoutTimeslot(timeslot))),
            ]);

            flash.success(`${events.length + timeslots.length} event(s) were blacked out`);

            const updateParams = {
                isGranted: isGranted,
            };

            // Update cache for blacked out events - fetch updated timeslots
            for (const [index] of events.entries()) {
                if (blackoutEventResults[index]) {
                    const timeslotParams = {
                        timeslot: null,
                        event: events[index],
                        productId: null,
                        startDate: null,
                    };

                    await updateTimeslotCache(timeslotParams, updateParams);
                }
            }

            // Remove blacked out timeslots from cache (they're no longer bookable)
            for (const [index, timeslot] of timeslots.entries()) {
                if (blackoutTimeslotResults[index]) {
                    removeCacheItem("timeslots", timeslot.id);
                }
            }
        } catch {
            flash.error("Something went wrong while blacking out events");
        }

        onClose();
    };

    return (
        <div>
            <Modal.Header>Blackout Events</Modal.Header>

            <Modal.Body>
                <span className="block space-y-4">
                    <p>
                        These events will no longer accept reservations, however existing reservations will be retained.
                    </p>
                    <p>
                        To get this capacity back, you would have to delete the blackout schedules from the product`s
                        settings
                    </p>
                </span>
            </Modal.Body>

            <Modal.Footer className="space-x-4">
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <SubmitButton isLoading={blackoutPending || blackoutTimeslotPending} onClick={handleSubmit}>
                    Confirm
                </SubmitButton>
            </Modal.Footer>
        </div>
    );
};
